import { useEffect, useState, useCallback } from "react";
import { fetchWithAuth } from "../../../../../../api-client/api_client";
import {
  CommonModelDiffResult,
  DiffCategoryTypes,
  DiffExecutionModelResult,
  IndividualDiffResult,
} from "../entities";
import { showErrorToast } from "../../../../../shared/Toasts";
import { computeCount } from "../helpers";

interface GetCommonModelDiffResultProps {
  commonModelDiffRunExecutionID: string;
  modelName: string;
  diffCategory?: DiffCategoryTypes;
  modelResult: DiffExecutionModelResult;
}

export const useGetCommonModelDiffResult = ({
  commonModelDiffRunExecutionID,
  modelName,
  diffCategory,
  modelResult,
}: GetCommonModelDiffResultProps) => {
  // state variables
  const [results, setResults] = useState<IndividualDiffResult[]>([]);
  const [lastKey, setLastKey] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalModelCount, setTotalModelCount] = useState<number | null>(null);

  // fetchData takes an pagination optional token. If token is null, it's the initial fetch.
  const fetchData = (paginationToken: any = null) => {
    setLoading(true);
    setError(null);

    let url = `/blueprints/cmd-execution/${commonModelDiffRunExecutionID}/${modelName}`;
    const params: Record<string, string> = {};

    if (paginationToken) {
      params.last_key = JSON.stringify(paginationToken);
    }
    if (diffCategory) {
      params.diff_category = diffCategory;
    }
    const queryString = new URLSearchParams(params).toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    fetchWithAuth({
      path: url,
      method: "GET",
      body: {},
      expectJSON: true,
      onResponse: (data: CommonModelDiffResult) => {
        setResults((prev) => (paginationToken ? [...prev, ...data.results] : data.results));
        setLastKey(data.last_key);
        setTotalModelCount(computeCount(modelResult, diffCategory));
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        error
          ?.json()
          .then((value: string | Record<string, any>) => {
            let errorMessage = "";
            if (typeof value == "string") {
              errorMessage = value;
            } else {
              errorMessage = value?.non_field_errors ?? JSON.stringify(value);
            }
            showErrorToast(
              `Failed to get Common Model diff results. Error message: \n ${errorMessage}`
            );
          })
          .catch(() => {
            showErrorToast("Failed to get Common Model diff results");
          });
      },
    });
  };

  // this effect triggers on mount and when diffCategory changes (e.g. someone is filtering the results). it resets the state and fetches the initial page of results.
  useEffect(() => {
    setResults([]);
    setLastKey(null);
    setTotalModelCount(null);
    fetchData();
  }, [diffCategory]);

  // loadMore fetches the next page using the current lastKey.
  const loadMore = useCallback(() => {
    if (lastKey) {
      fetchData(lastKey);
    }
  }, [lastKey]);

  return { results, totalModelCount, lastKey, loading, error, loadMore };
};
